import React, { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MeasurementsList, DiagnosticsList, CommandsList, DiagnosticsPicker } from "Components/overview";
import MoreCommandButton from "./MoreCommandButton";
import {
  useSystemConfig,
  useSettingsUI,
  useDataTimeStamp,
  useServiceMode,
  useExtraDiagnostics,
  useSolutionType,
  useDevicesList,
  useDeviceConfigEntries,
  useSystemId,
} from "../../../hooks/useReduxSelectors";
import { selectPincodeEnabled, selectPinUnlocked } from "../../../ducks/authenticationDuck";
import { useMsal } from "Auth/msal-context";
import DetailedDeviceModal from "./DetailedDeviceModal";
import ParameterConfigurationModal from "./ParameterConfigurationModal";
import { refreshSignalR } from "../../../../Dashboard/ducks/devicesDuck";
import { Box, Card, IconButton, Popover, Typography, CircularProgress } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";
import RenameLoraSensorModal from "./RenameLoraSensorModal";
import WarningIcon from '@mui/icons-material/Warning';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const DeviceItem = ({ hwId, isCompact }) => {
  const systemId = useSystemId();
  const config = useSystemConfig();
  const settingsUI = useSettingsUI();
  const showLinearGauges = settingsUI?.showLinearGauges;
  const showDeviceTags = settingsUI?.showDeviceTags;
  const dataTimeStamp = useDataTimeStamp();
  const serviceMode = useServiceMode();
  const extraDiagnostics = useExtraDiagnostics();
  const solutionType = useSolutionType();
  const { isEdge } = useMsal();
  const pincodeEnabled = useSelector(selectPincodeEnabled);
  const pinUnlocked = useSelector(selectPinUnlocked);
  const isServiceModeLocked = isEdge && pincodeEnabled && !pinUnlocked;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isParameterConfigurationModalOpen, setIsParameterConfigurationModalOpen] = useState(false);
  const [isRenameLoraSensorModalOpen, setIsRenameLoraSensorModalOpen] = useState(false);
  // Only used for independent devices, where the command list visibility is manually toggled.
  const [commandsExpanded, setCommandsExpanded] = useState(false);

  const [dataIsRecent, setDataIsRecent] = useState(false);
  const [timeToCheckData, setTimeToCheckData] = useState(false);

  //POPOVER
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const dispatch = useDispatch();

  const devices = useDevicesList();
  const device = (devices && devices.length > 0 && devices.find((dev) => dev.hwId === hwId)) || {};

  //console.log("device", device);

  const devicesEntries = useDeviceConfigEntries();
  const deviceConfigs =
    devicesEntries && devicesEntries.length > 0 && devicesEntries.find((dev) => device && dev.hwId === device.hwId);

  const onInfoClicked = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onParameterConfigurationViewClicked = useCallback(() => {
    setIsParameterConfigurationModalOpen(true);
  }, []);

  const onLoraNameTitleClicked = useCallback(() => {
    setIsRenameLoraSensorModalOpen(true);
  }, []);

  const sensorSolution = solutionType === "lora";
  useEffect(() => {
    if (systemId !== "edge" && config && sensorSolution) {
      dispatch(
        refreshSignalR({
          hwId: device.hwId,
          payload: {
            ...config,
          },
          delay: 1000,
        })
      );
    }
  }, []);

  const compareLastFetchedDataTimeToNow = () => {
    let unixTimeNowInSeconds = new Date().getTime() / 1000;
    let unixTimeLastDataTimestamp = 0;

    if (isNaN(dataTimeStamp)) {
      // convert retrieved timestamp to unix if it's in date format
      let stringToDate = new Date(dataTimeStamp);
      unixTimeLastDataTimestamp = stringToDate.getTime();
      unixTimeLastDataTimestamp = unixTimeLastDataTimestamp / 1000;
    } else {
      unixTimeLastDataTimestamp = dataTimeStamp;
    }

    let timeBetweenNowAndLastData = Math.floor(Math.abs(unixTimeNowInSeconds) - Math.abs(unixTimeLastDataTimestamp));

    if (solutionType === "lora" && timeBetweenNowAndLastData > 43200) {
      //43200s/12hours --- lora
      console.log(
        "Lora devices' data is over 12 hours old. Last data is from",
        Math.floor(timeBetweenNowAndLastData / 60),
        "minutes ago"
      );
      setDataIsRecent(false);
    } else if (solutionType !== "lora" && timeBetweenNowAndLastData > 120) {
      //120s/2min --- other than lora
      console.log(
        "Devices' data is over 2 min old. Last data is from",
        Math.floor(timeBetweenNowAndLastData / 60),
        "minutes ago"
      );
      setDataIsRecent(false);
    } else {
      setDataIsRecent(true);
    }
  };

  // Check if the device is receiving data by checking if it's payload contains measurement or diagnostic unit value
  useEffect(() => {
    const checkValues = () => {
      device &&
        device.measurements &&
        device.measurements.map((measurement) => {
          if (measurement.unit !== undefined && measurement.unit !== null) {
            setDataIsRecent(true);
          }
        });
      device &&
        device.diagnostics &&
        device.diagnostics.map((diagnostic) => {
          if (diagnostic.unit !== undefined && diagnostic.unit !== null) {
            setDataIsRecent(true);
          }
        });
    };
    if (dataIsRecent === false) {
      checkValues();
    }
  }, [device]);

  // Turn device cards to grey immediately, if they contain old data from browser cache memory. Via compareLastFetchedDataTimeToNow()
  // Start a timed function that sets timeToCheckData(true) once every 5min.
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeToCheckData(true);
    }, 300000);

    if (config === undefined) {
      // if edge unit is not receiving data, its config may be undefined, we want to fire compareLastFetchedDataTimeToNow() in that case
      compareLastFetchedDataTimeToNow();
    }
    if (config !== undefined) {
      if (config.APP_ENV === "Edge") {
        compareLastFetchedDataTimeToNow();
        return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks
      }
    }
  }, []);

  useEffect(() => {
    if (timeToCheckData === true) {
      if (config !== undefined) {
        if (config.APP_ENV === "Edge") {
          compareLastFetchedDataTimeToNow();
        }
      }
    }
    setTimeToCheckData(false);
  }, [timeToCheckData]); // every 5min


  const bgColorMap = { green: "primary.main", orange: "warning.main", red: "error.main" };

  // dataIsRecent is driven by a system-wide timestamp (see compareLastFetchedDataTimeToNow), not by
  // this specific device's own data, so it can flip true even for a device that never sent anything.
  // Only color/enable the card once this device has actually reported at least one real value.
  const receivedValueCount =
    (device?.measurements?.filter((m) => m.unit !== undefined && m.unit !== null).length || 0) +
    (device?.diagnostics?.filter((d) => d.unit !== undefined && d.unit !== null).length || 0);
  const hasReceivedAnyData = receivedValueCount > 0;

  const sidebarBg = dataIsRecent && hasReceivedAnyData && device?.deviceBgColor ? bgColorMap[device.deviceBgColor] || "dark.main" : "dark.main";

  // A device reporting only a single value (e.g. just "Communication: FAIL") is showing that it isn't
  // fully online yet, even though dataIsRecent is already true so the value itself can be displayed.
  // Keep the loading spinner visible until we've received at least 2 distinct values from the device.
  const showLoadingSpinner = !dataIsRecent || receivedValueCount < 2;

  // If the ONLY value ever received from this device is the "Communication" diagnostic itself (i.e. it
  // reported comms failure and nothing else), don't paint the sidebar fully red like an active alarm on
  // an otherwise-online device. Use a grey background with 45deg red stripes instead, to visually
  // distinguish "never actually connected" from "connected then alarmed".
  const communicationDiagnostic = device?.diagnostics?.find((d) => d.valueName === "Communication");
  const communicationOnlyFail =
    device?.deviceBgColor === "red" &&
    receivedValueCount === 1 &&
    communicationDiagnostic?.unit !== undefined &&
    communicationDiagnostic?.unit !== null;

  return (
    <>
      <Card
        sx={(theme) => ({
          mb: 1.5,
          mr: 0.5,
          position: "relative",
          overflow: "hidden",
          bgcolor: theme.palette.dashboard.cardBackground,
          pl: "40px",
        })}
      >
        <Box
          sx={(theme) => {
            const resolvedColor = sidebarBg.split(".").reduce((obj, key) => obj?.[key], theme.palette) ?? theme.palette.grey[600];
            const neutralGrey = theme.palette.dark?.main ?? theme.palette.grey[600];
            return {
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "40px",
              background: communicationOnlyFail
                ? `repeating-linear-gradient(45deg, ${neutralGrey} 0px, ${neutralGrey} 8px, ${theme.palette.error.main} 8px, ${theme.palette.error.main} 16px)`
                : resolvedColor,
            };
          }}
        />
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              noWrap
              sx={{
                color: dataIsRecent && hasReceivedAnyData ? "text.primary" : "text.disabled",
                cursor: sensorSolution ? "pointer" : "default",
                ml: 2
              }}
              onDoubleClick={sensorSolution ? onLoraNameTitleClicked : undefined}
            >
              {deviceConfigs?.name}
            </Typography>
            {showLoadingSpinner && (
              <CircularProgress size={24} sx={{ ml: 1 }} />
            )}
            {showDeviceTags && deviceConfigs?.tag && (
              <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }} noWrap>
                {deviceConfigs.tag}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            {deviceConfigs?.hasParameters && (
              <IconButton onClick={onParameterConfigurationViewClicked}>
                <SettingsIcon font />
              </IconButton>
            )}
            <IconButton onClick={handleOpen} >
              <MoreVertIcon font />
            </IconButton>
            <IconButton onClick={onInfoClicked}>
              <InfoIcon />
            </IconButton>
            {deviceConfigs?.independent && deviceConfigs?.commands?.length > 0 && (
              <MoreCommandButton
                hwId={device?.hwId}
                independent={deviceConfigs?.independent}
                isExpanded={commandsExpanded}
                onToggle={() => setCommandsExpanded(prev => !prev)}
                disabled={isServiceModeLocked}
              />
            )}
          </Box>
        </Box>

        {/* Diagnostics row */}
        {device?.diagnostics?.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.5,
              bgcolor: device.diagnosticsPickers?.length > 0 ? "surface.panelDark" : "transparent",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <DiagnosticsList
                diagnosticsPickers={device?.diagnosticsPickers}
                dataIsRecent={dataIsRecent}
              />
            </Box>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "center", horizontal: "left" }}
              transformOrigin={{ vertical: "center", horizontal: "right" }}
              slotProps={{ paper: { sx: { minWidth: 400, padding: "8px" } } }}
            >
              <DiagnosticsPicker hwId={device?.hwId} />
            </Popover>
          </Box>
        ) : null}

        {/* Measurements */}
        <Box sx={{ px: 1.5, py: 1 }}>
          <MeasurementsList
            combineMeasureAndCommands={device?.measurements}
            measurementTags={deviceConfigs?.measurements}
            solutionType={solutionType}
            showDeviceTags={showDeviceTags}
            showLinearGauges={showLinearGauges}
            dataIsRecent={dataIsRecent}
            config={config}
            isCompact={isCompact}
          />
        </Box>

        {/* Commands */}
        {deviceConfigs?.commands?.length > 0 &&
          !isServiceModeLocked &&
          (deviceConfigs?.independent
            ? commandsExpanded
            : (serviceMode.isActive && serviceMode.status === "ManualActive")) && (
            <Box sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
              <CommandsList
                hwId={device?.hwId}
                commands={deviceConfigs?.commands}
                diagnostics={device.diagnostics}
              />
            </Box>
          )}
      </Card >
      <DetailedDeviceModal
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        title={deviceConfigs && deviceConfigs.name}
        diagnostics={device.diagnostics}
        extraDiagnostics={(extraDiagnostics && extraDiagnostics[hwId]) || {}}
        measurements={device.measurements}
        color={device.deviceBgColor}
        systemId={systemId}
        hwId={hwId}
        isLora={solutionType === "lora"}
      />
      {
        deviceConfigs?.hasParameters && deviceConfigs.hasParameters === true && (
          <ParameterConfigurationModal
            show={isParameterConfigurationModalOpen}
            onHide={() => setIsParameterConfigurationModalOpen(false)}
            systemId={systemId}
            hwId={hwId}
            displayName={deviceConfigs && deviceConfigs.name}
            solutionType={solutionType}
          />
        )
      }
      {
        solutionType === "lora" && (
          <RenameLoraSensorModal
            show={isRenameLoraSensorModalOpen}
            onHide={() => setIsRenameLoraSensorModalOpen(false)}
            systemId={systemId}
            hwId={hwId}
            displayName={deviceConfigs && deviceConfigs.name}
          />
        )
      }
    </>
  );
};

export default DeviceItem;
